import type { AnalyticsEvent, ContextAttrs, UserAttrs, ItemAttrs, Platform } from "~/types/analytics";
import type { User } from "~/types/user";
import { getAnalyticsBuffer, EventPriority } from "./analytics-buffer";

/**
 * Helper class to construct and send analytics events
 */
export class AnalyticsEventBuilder {
  /**
   * Get the current platform based on user agent
   */
  static getPlatform(): Platform {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|ipod/.test(ua);
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);

    if (isMobile) {
      if (isIOS) return "ios_web";
      if (isAndroid) return "android_web";
    }
    return "desktop_web";
  }

  /**
   * Get current network type if available
   */
  static getNetworkType(): string | undefined {
    // @ts-expect-error - connection is not standard but widely supported
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return connection?.effectiveType;
  }

  /**
   * Create base context attributes
   * @param channel - JSON string from deal-channel.ts builders (e.g., buildShopChannel, buildSearchChannel)
   * @param position - Position in feed/list
   * @param options - Optional deeplink information
   */
  static createContextAttrs(
    channel: string,
    position: number,
    options?: {
      deeplink_url?: string;
      deeplink_feature?: string;
      deeplink_campaign?: string;
    }
  ): ContextAttrs {
    return {
      c_platform: this.getPlatform(),
      c_channel: channel,
      c_position: position,
      c_net: this.getNetworkType(),
      c_deeplink_url: options?.deeplink_url ?? null,
      c_deeplink_feature: options?.deeplink_feature ?? null,
      c_deeplink_campaign: options?.deeplink_campaign ?? null,
    };
  }

  /**
   * Create user attributes from User object
   * @param user - User object from user store
   */
  static createUserAttrs(user: User): UserAttrs {
    const shippingAddr = user.addresses?.shipping;

    return {
      u_name: user.basicInfo?.name || "Guest User",
      u_email: user.basicInfo?.email || "guest@example.com",
      u_gender: undefined, // Not available in User type
      u_age: undefined, // Not available in User type
      u_country: shippingAddr?.country || "US",
      u_state: shippingAddr?.state || "",
      u_city: shippingAddr?.city || "",
      u_zip: shippingAddr?.zip || "",
      u_is_guest: !user.signedUp,
    };
  }

  /**
   * Create item attributes
   */
  static createItemAttrs(item: {
    currency: string;
    subsidy?: number | null;
    cost?: number | null;
    price_dash?: number | null;
    price_detail_buy_together?: number | null;
    price_detail_join?: number | null;
    price_detail_buy_alone?: number | null;
    price_checkout?: number | null;
    buying_type?: string | null;
  }): ItemAttrs {
    return {
      i_quantity: 1,
      i_currency: item.currency,
      i_subsidy: item.subsidy ?? null,
      i_cost: item.cost ?? null,
      i_price_dash: item.price_dash ?? null,
      i_price_detail_buy_together: item.price_detail_buy_together ?? null,
      i_price_detail_join: item.price_detail_join ?? null,
      i_price_detail_buy_alone: item.price_detail_buy_alone ?? null,
      i_price_checkout: item.price_checkout ?? null,
      i_buying_type: item.buying_type ?? null,
    };
  }

  /**
   * Send an impression event (buffered by default)
   * @param userId - User ID
   * @param dealId - Deal ID
   * @param productId - Product ID
   * @param reqId - Request ID from the deal object (provided by backend)
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   * @param itemAttrs - Item attributes
   */
  static async sendImpressionEvent(
    userId: string,
    dealId: string,
    productId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs,
    itemAttrs: ItemAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "impression",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
      i_deal_id: dealId,
      i_product_id: productId,
      i_attrs: itemAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.BUFFERED);
  }

  /**
   * Send a view detail event (buffered by default)
   * @param userId - User ID
   * @param dealId - Deal ID
   * @param productId - Product ID
   * @param reqId - Request ID from the deal object (provided by backend)
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   * @param itemAttrs - Item attributes
   */
  static async sendViewDetailEvent(
    userId: string,
    dealId: string,
    productId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs,
    itemAttrs: ItemAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "view_detail",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
      i_deal_id: dealId,
      i_product_id: productId,
      i_attrs: itemAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.BUFFERED);
  }

  /**
   * Send a purchase event (sent immediately, bypasses buffer)
   * @param userId - User ID
   * @param dealId - Deal ID
   * @param productId - Product ID
   * @param reqId - Request ID from the deal object (provided by backend)
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   * @param itemAttrs - Item attributes
   */
  static async sendPurchaseEvent(
    userId: string,
    dealId: string,
    productId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs,
    itemAttrs: ItemAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "purchase",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
      i_deal_id: dealId,
      i_product_id: productId,
      i_attrs: itemAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.IMMEDIATE);
  }

  /**
   * Send a checkout summary event (buffered)
   * @param userId - User ID
   * @param dealId - Deal ID
   * @param productId - Product ID
   * @param reqId - Request ID from the deal object (provided by backend)
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   * @param itemAttrs - Item attributes
   */
  static async sendCheckoutSummaryEvent(
    userId: string,
    dealId: string,
    productId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs,
    itemAttrs: ItemAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "checkout_summary",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
      i_deal_id: dealId,
      i_product_id: productId,
      i_attrs: itemAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.BUFFERED);

    // No first-party mirror: checkout_summary is a checkout-page-load event, already covered by the
    // page_view autocapture (query by route /deal/checkout).
  }

  /**
   * Send a select payment event (buffered)
   * @param userId - User ID
   * @param dealId - Deal ID
   * @param productId - Product ID
   * @param reqId - Request ID from the deal object (provided by backend)
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   * @param itemAttrs - Item attributes
   */
  static async sendSelectPaymentEvent(
    userId: string,
    dealId: string,
    productId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs,
    itemAttrs: ItemAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "select_payment",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
      i_deal_id: dealId,
      i_product_id: productId,
      i_attrs: itemAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.BUFFERED);

    // No first-party mirror here: the funnel's payment step is captured as payment_method_select
    // (track-events.ts) — mirroring select_payment too would double-report it.
  }

  /**
   * Send a membership trial start event (sent immediately)
   * @param userId - User ID
   * @param abIds - Active AB test IDs
   * @param contextAttrs - Context attributes
   * @param userAttrs - User attributes
   */
  static async sendMembershipTrialStartEvent(
    userId: string,
    reqId: string,
    abIds: string[],
    contextAttrs: ContextAttrs,
    userAttrs: UserAttrs
  ): Promise<void> {
    const event: AnalyticsEvent = {
      event_type: "membership_trial_start",
      event_time: Math.floor(Date.now() / 1000),
      req_id: reqId,
      ab_ids: abIds,
      c_attrs: contextAttrs,
      u_user_id: userId,
      u_attrs: userAttrs,
    };

    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, EventPriority.IMMEDIATE);
  }

  /**
   * Send a custom event with optional priority control
   * @param event - The analytics event to send
   * @param priority - Optional priority (defaults to auto-detect based on event type)
   */
  static async sendCustomEvent(event: AnalyticsEvent, priority?: EventPriority): Promise<void> {
    const buffer = getAnalyticsBuffer();
    await buffer.addEvent(event, priority);
  }

  /**
   * Manually flush all buffered events
   * Useful for ensuring events are sent before navigation or critical operations
   */
  static async flushBuffer(): Promise<void> {
    const buffer = getAnalyticsBuffer();
    await buffer.flush();
  }
}

/**
 * Export the buffer for direct access if needed
 */
export { getAnalyticsBuffer, EventPriority } from "./analytics-buffer";
